import Footer from "components/Footer/Footer.jsx";
import ScrollToTop from "components/ScrollToTop";
import TrialEndingBanner from "components/TrialEndingBanner";
import Loader from "components/Loader/Loader";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import routes from "routes.js";

import AdminNavbar from "../../custom_modules/AdminNavbar";
import AppOnboardingWrapper from "../../custom_modules/AppOnboarding";
import OnboardingWrapper from "../../custom_modules/OnboardingNew";
import Sidebar from "../../custom_modules/Sidebar";
import { generateGroupedLinks } from "../../custom_modules/Sidebar/sidebarHelpers";
import { addPath } from "../../filterReducer";
import { PrivateRoute } from "../../modules/privateRoute";
import { WeekConfigProvider, useWeekConfig } from "../../utils/weekConfig";
import BottomNavBar from "../../components/BottomNavBar";
import MobileMenuDrawer from "../../components/MobileMenuDrawer";
import MobileTopBar from "../../components/MobileTopBar";
import QuickActionsPanel from "../../custom_modules/QuickActions/QuickActionsPanel";
import { useResponsive } from "../../customHooks/useResponsive";


const AdminContent = props => {
  const dispatch = useDispatch();
  const { isWeekStartReady } = useWeekConfig();

  const [sidebarMini, setSidebarMini] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [filteredRoutes, setFilteredRoutes] = useState(routes);
  const [daysLeftInTrial, setDaysLeftInTrial] = useState(null);
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);

  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const quickActionsAnchorRef = useRef(null);

  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  const adminHistory = useHistory();
  const location = useLocation();

  const toggleQuickActions = () => {
    setIsQuickActionsOpen(prev => !prev);
  };

  const closeQuickActions = () => setIsQuickActionsOpen(false);
  const wrapperRef = useRef(null);

  const { selectedOrganization } = props;

  // Check if current plan is free and user is owner
  const isFreePlan = useMemo(() => {
    const isFree =
      selectedOrganization?.plan?.name === "Free" ||
      selectedOrganization?.plan?.nick_name === "standard-free" ||
      selectedOrganization?.plan?.nick_name === "elite-free";
    const isOwner = selectedOrganization?.role === "owner";
    return isFree && isOwner;
  }, [
    selectedOrganization?.plan?.name,
    selectedOrganization?.plan?.nick_name,
    selectedOrganization?.role,
  ]);

  // Redirect based on plan for non-admin/owner
  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization.role !== "admin" &&
      selectedOrganization.role !== "owner" &&
      adminHistory?.location?.pathname === "/user/dashboard"
    ) {
      const isStandardPlan =
        selectedOrganization.plan &&
        (selectedOrganization.plan.name === "Standard" ||
          selectedOrganization.plan.nick_name === "standard-free");

      if (isStandardPlan) {
        adminHistory.push("/user/shift-and-schedule");
      } else {
        // Use "tasks" for operation type (default), "jobs" for service type
        const orgType = selectedOrganization.type || "operation";
        const tasksPath =
          orgType === "operation" ? "/user/tasks" : "/user/jobs";
        adminHistory.push(tasksPath);
      }
    }
  }, [selectedOrganization, adminHistory?.location?.pathname]);

  // Initialize Crisp chat for desktop owner only
  useEffect(() => {
    if (isMobileOrTablet) {
      // Hide Crisp if it was already loaded (e.g. user resized from desktop)
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:hide"]);
      }
      return;
    }

    if (localStorage.getItem("selected_organization_role") === "owner") {
      if (!window.$crisp) {
        window.$crisp = [];
      }
      window.CRISP_WEBSITE_ID = "99c3a618-53c4-44b5-8e95-dc28db5a70a7";
      (function () {
        var d = document;
        var s = d.createElement("script");
        s.id = "crisp-chat-js";
        s.src = "https://client.crisp.chat/l.js";
        s.async = 1;
        if (!d.getElementById("crisp-chat-js")) {
          d.getElementsByTagName("head")[0].appendChild(s);
        }
      })();
      window.$crisp.push(["do", "chat:show"]);
      if (localStorage.getItem("user_fullName")) {
        window.$crisp.push([
          "set",
          "user:nickname",
          localStorage.getItem("user_fullName"),
        ]);
      }
      if (localStorage.getItem("email")) {
        window.$crisp.push([
          "set",
          "user:email",
          localStorage.getItem("email"),
        ]);
      }
    }
  }, [isMobileOrTablet]);

  // Fetch subscription and calculate remaining trial days
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      props.getOrganizationSubscription({
        organization_id: selectedOrganization.id,
      });
    }
  }, [selectedOrganization?.id]);

  // Calculate remaining trial days from subscription data
  useEffect(() => {
    if (props.currentSubscription && props.currentSubscription.trial_till) {
      const trialEndDate = moment(props.currentSubscription.trial_till);
      const today = moment();
      const daysRemaining = trialEndDate.diff(today, "days");
      
      if (daysRemaining < 0) {
        setDaysLeftInTrial(null); // null means trial has ended
      } else {
        setDaysLeftInTrial(daysRemaining);
      }
    }
  }, [props.currentSubscription]);

  // Determine display message and button visibility
  const trialEndingBannerProps = (() => {
    let displayMessage;
    if (daysLeftInTrial === null) {
      displayMessage = "Free trial has ended";
    } else if (daysLeftInTrial === 0) {
      displayMessage = "Free trial ends today";
    } else if (daysLeftInTrial === 1) {
      displayMessage = "Free trial ends tomorrow";
    } else {
      displayMessage = `Free trial ends in ${daysLeftInTrial} days`;
    }
    
    return {
      displayMessage,
      showButton: true, // Always show button when banner is shown
    };
  })();

  // Fetch org list once on mount — shared between Sidebar (desktop) and MobileTopBar popup (mobile)
  useEffect(() => {
    props.getOrganizationList();
  }, []);

  // On mobile/tablet the Sidebar is not rendered, so it cannot select the organization.
  // This effect mirrors the Sidebar's initialization logic for mobile/tablet views.
  useEffect(() => {
    if (!isMobileOrTablet) return;
    const list = props.organizations?.list;
    if (!list?.length) return;
    if (props.organizations?.selectedOrganization) return;

    const savedId = localStorage.getItem("selected_organization_id");
    let orgToSelect = savedId ? list.find(org => org.id === savedId) : null;
    if (!orgToSelect) {
      orgToSelect = list.find(org => org.is_active) || null;
    }
    if (orgToSelect) {
      localStorage.setItem("selected_organization", orgToSelect.name);
      localStorage.setItem("selected_organization_id", orgToSelect.id);
      localStorage.setItem("selected_organization_role", orgToSelect.role);
      props.selectOrganization(orgToSelect);
    }
  }, [props.organizations?.list, selectedOrganization, isMobileOrTablet]);

  // Save organization config to localStorage
  useEffect(() => {
    if (selectedOrganization?.plan?.id) {
      localStorage.setItem(
        "selected_organization_plan",
        selectedOrganization.plan.name,
      );
    }
    if (selectedOrganization?.configuration?.id) {
      localStorage.setItem(
        "date_format",
        selectedOrganization.configuration.date_format,
      );
      localStorage.setItem(
        "start_day_of_week",
        selectedOrganization.configuration.start_day_of_week,
      );
    }
  }, [selectedOrganization]);

  // Scroll to top on route change
  useEffect(() => {
    return adminHistory.listen((location, action) => {
      if (action === "PUSH" && wrapperRef?.current) {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
        wrapperRef.current.scrollTop = 0;
      }
    });
  }, [adminHistory]);

  // Filter routes for projects and jobs based on organization type
  useEffect(() => {
    if (!selectedOrganization || !selectedOrganization.id) {
      setFilteredRoutes(routes);
      return;
    }

    const orgType = selectedOrganization.type || "operation";

    const filterRoutes = routesArr => {
      return routesArr
        .map(route => {
          if (route.collapse && route.views) {
            return { ...route, views: filterRoutes(route.views) };
          }
          return route;
        })
        .filter(route => {
          if (route.collapse && route.views && route.views.length === 0)
            return false;
          // If route has no type, include it (common routes)
          if (!route.type) return true;
          // Check if route type matches organization type
          return route.type === orgType;
        });
    };

    const allowedRoutes = filterRoutes(routes);
    setFilteredRoutes(allowedRoutes);
  }, [selectedOrganization]);

  const getRoutes = routesArray => {
    const routes = [];
    routesArray.forEach((prop, key) => {
      if (prop.collapse && prop.views) {
        // Flatten nested routes from collapse views
        const nestedRoutes = getRoutes(prop.views);
        routes.push(...nestedRoutes);
      } else if (prop.layout === "/user") {
        routes.push(
          <PrivateRoute
            key={prop.path || key}
            path={prop.layout + prop.path}
            restricted={prop.restricted}
            selectedOrganization={selectedOrganization}
            component={prop.component}
          />,
        );
      }
    });
    return routes;
  };

  const sidebarLinks = useMemo(
    () => generateGroupedLinks(filteredRoutes, selectedOrganization),
    [filteredRoutes, selectedOrganization],
  );

  const handleMiniClick = () => {
    setSidebarMini(!sidebarMini);
    document.body.classList.toggle("sidebar-mini");
  };

  const handleNavOpen = () => setNavOpen(!navOpen);
  const clearNavOpen = () => setNavOpen(false);

  const handleUpgradeClick = () => {
    adminHistory.push("/user/subscription");
  };

  useEffect(() => {
    dispatch(addPath(location.pathname));
  }, [location.pathname, dispatch]);

  // if (!isWeekStartReady) {
  //   return <div style={{ padding: "2rem", textAlign: "center" }} />;
  // }

  return (
    <OnboardingWrapper
      selectedOrganization={selectedOrganization}
      userRole={props.userRole}
      onInviteMember={props.onInviteMember}
      isInviteLoading={props.isInviteLoading}
      memberInvited={props.memberInvited}
      clearMemberInvited={props.clearMemberInvited}
      updateFirstLogin={props.updateFirstLogin}
      loginProfileCreatedReset={props.loginProfileCreatedReset}
    >
      {!isWeekStartReady ? (
        <div style={{ padding: "2rem", textAlign: "center" }}></div>
      ) : (
        <AppOnboardingWrapper
          selectedOrganization={selectedOrganization}
          onInviteMember={props.onInviteMember}
          isInviteLoading={props.isInviteLoading}
          appOnboardingState={props.appOnboardingState}
          fetchAppOnboarding={props.fetchAppOnboarding}
          completeAppOnboardingStep={props.completeAppOnboardingStep}
          isMobile={isMobile}
          isTablet={isTablet}
        >
          <div
            className={`wrapper${isMobileOrTablet ? " wrapper-mobile" : ""}`}
            ref={wrapperRef}
            style={{
              paddingBottom: isMobile
                ? "calc(var(--app-onboarding-panel-height, 0px) + 72px)"
                : isTablet
                ? "calc(var(--app-onboarding-panel-height, 0px) + 80px)"
                : "var(--app-onboarding-panel-height, 0px)",
            }}
          >
            {!isMobileOrTablet && (
              <Sidebar
                {...props}
                routes={filteredRoutes}
                handleMiniClick={handleMiniClick}
                sidebarMini={sidebarMini}
                navOpen={navOpen}
                clearNavOpen={clearNavOpen}
                handleNavOpen={handleNavOpen}
              />
            )}

            {isMobileOrTablet && (
              <>
                <MobileTopBar
                  profile={props.profile}
                  organizations={props.organizations}
                  selectedOrganization={selectedOrganization}
                  selectOrganization={props.selectOrganization}
                  loginProfileCreatedReset={props.loginProfileCreatedReset}
                  history={adminHistory}
                />
                <BottomNavBar
                  onMenuClick={() => setMenuDrawerOpen(o => !o)}
                  menuOpen={menuDrawerOpen}
                  routes={filteredRoutes}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  onQuickActionsToggle={toggleQuickActions}
                  quickActionsAnchorRef={quickActionsAnchorRef}
                  selectedOrganization={props.selectedOrganization}
                />
                <QuickActionsPanel
                  isOpen={isQuickActionsOpen}
                  onClose={closeQuickActions}
                  selectedOrganization={props.selectedOrganization}
                  anchorRef={quickActionsAnchorRef}
                />
                <MobileMenuDrawer
                  isOpen={menuDrawerOpen}
                  onClose={() => setMenuDrawerOpen(false)}
                  routes={filteredRoutes}
                  selectedOrganization={selectedOrganization}
                  history={adminHistory}
                />
              </>
            )}

            <div 
              className={`main-panel${isMobileOrTablet ? "main-panel-mobile" : ""}`}
              style={{
                height: !isMobileOrTablet ? "calc(100vh - var(--app-onboarding-panel-height, 0px))" : "auto",
                overflowY: !isMobileOrTablet ? "auto" : "initial",
              }}
            >
              {!isMobileOrTablet && (
                <AdminNavbar
                  sidebarState={sidebarMini}
                  {...props}
                  handleMiniClick={handleMiniClick}
                  handleNavOpen={handleNavOpen}
                />
              )}
              {(isFreePlan || props.currentSubscription?.status === "trialing" || (daysLeftInTrial === null && isFreePlan && props.currentSubscription?.trial_till)) && props.currentSubscription && (
                <TrialEndingBanner 
                  displayMessage={trialEndingBannerProps.displayMessage}
                  showButton={trialEndingBannerProps.showButton}
                  onUpgradeClick={handleUpgradeClick}
                />
              )}
              <Switch>
                {getRoutes(filteredRoutes)}
                <Route path="/user/*">
                  <Redirect to="/user/dashboard" />
                </Route>
              </Switch>
              <ScrollToTop />
            </div>
          </div>
        </AppOnboardingWrapper>
      )}
    </OnboardingWrapper>
  );
};

const Admin = props => {
  return (
    <WeekConfigProvider selectedOrganization={props.selectedOrganization}>
      <AdminContent {...props} />
    </WeekConfigProvider>
  );
};

export default Admin;